import React, { useState } from "react";
import { initializeApp } from "firebase/app";
import {
  getAuth,
  createUserWithEmailAndPassword,
  updateProfile,
  deleteUser,
} from "firebase/auth";
import { getFirebaseErrorMessage, getBackendErrorMessage } from "../utils/firebaseErrors";
import { BACKEND_URL } from '../utils/config';

// Password validation rules
const passwordRules = {
  minLength: (pwd) => pwd.length >= 8,
  hasNumber: (pwd) => /\d/.test(pwd),
  hasUppercase: (pwd) => /[A-Z]/.test(pwd),
  hasLowercase: (pwd) => /[a-z]/.test(pwd),
  hasSymbol: (pwd) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pwd),
};

const validatePassword = (pwd) => {
  return {
    minLength: passwordRules.minLength(pwd),
    hasNumber: passwordRules.hasNumber(pwd),
    hasUppercase: passwordRules.hasUppercase(pwd),
    hasLowercase: passwordRules.hasLowercase(pwd),
    hasSymbol: passwordRules.hasSymbol(pwd),
  };
};

const isPasswordValid = (pwd) => {
  const rules = validatePassword(pwd);
  return Object.values(rules).every((rule) => rule === true);
};

const FIREBASE_CONFIG = process.env.REACT_APP_FIREBASE === 'development' ? {
  apiKey: "AIzaSyCk2KLEPKnILl9SJ7lT9frBdKS0o9Dv560",
  authDomain: "connection-healthcare-app.firebaseapp.com",
  projectId: "connection-healthcare-app",
  storageBucket: "connection-healthcare-app.firebasestorage.app",
  messagingSenderId: "559258061369",
  appId: "1:559258061369:web:7d9ca98231cb58a93fdbe8",
  measurementId: "G-DKPC3TR8YZ",
} : {
  apiKey: "AIzaSyDPPnT8Ms89no1-hDXqbQsZrEI5UDEouzA",
  authDomain: "connection-healthcare-app-prod.firebaseapp.com",
  projectId: "connection-healthcare-app-prod",
  storageBucket: "connection-healthcare-app-prod.firebasestorage.app",
  messagingSenderId: "206198908104",
  appId: "1:206198908104:web:0d62c4a95fa7d4302631fd"
};

//...
export default function CreateAccountForm({
  title,
  buttonText,
  endpoint,
  showCode = false,
  onSuccess,
  onNavigateToLogin,
  onNavigateToOtherPage,
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [code, setCode] = useState("");
  const [message, setMessage] = useState(null);
  const [isError, setIsError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [passwordRulesStatus, setPasswordRulesStatus] = useState({
    minLength: false,
    hasNumber: false,
    hasUppercase: false,
    hasLowercase: false,
    hasSymbol: false,
  });

  const handlePasswordChange = (e) => {
    const pwd = e.target.value;
    setPassword(pwd);
    setPasswordRulesStatus(validatePassword(pwd));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    setMessage(null);
    setIsError(false);

    // Validate password before submission
    if (!isPasswordValid(password)) {
      setMessage("Password does not meet all requirements. Please check the rules below.");
      setIsError(true);
      return;
    }

    // Validate code if required
    if (showCode && !code.trim()) {
      setMessage("Invitation code is required");
      setIsError(true);
      return;
    }

    try {
      initializeApp(FIREBASE_CONFIG);
      const auth = getAuth();
      // 1) Create Firebase user on the client
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );

      // 2) update profile with displayName
      if (displayName) {
        await updateProfile(userCredential.user, { displayName });
      }
      
      // 3) Build payload
      const payload = {
        user: {
          username: displayName,
          email: userCredential.user.email,
          uid: userCredential.user.uid,
          first_name: firstName,
          last_name: lastName,
          phone_number: phoneNumber,
          date_of_birth: dateOfBirth,
        },
        token: userCredential.user.accessToken,
      };

      // Add code if this is for PSW
      if (showCode) {
        payload.code = code.trim();
      }

      // 3) call your backend to create user
      const res = await fetch(`${BACKEND_URL}${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (res.status === 201) {
        setMessage(`${title} created successfully! Redirecting to login...`);
        setTimeout(() => onSuccess(), 500);
        return;
      }

      if (!res.ok) {
        // If backend call fails, try to delete the Firebase user
        try {
          const user = userCredential.user;
          await deleteUser(user);
        } catch (deleteError) {
          console.error("Failed to delete Firebase user:", deleteError);
          // This is a fallback. Ideally, the backend should handle this cleanup.
          // We'll show a generic error to the user.
        }
        
        const text = await res.text();
        const errorMessage = getBackendErrorMessage(res.status, text);
        throw new Error("Failed to create account. Please contact support if this issue persists.");
      }

      const data = await res.json();
      // setMessage(`User saved in DB (uid=${data.username})`);
      setIsError(false);
    } catch (err) {
      let errorMessage;
      
      // Check if it's a Firebase auth error (err.code or err.message contains auth/)
      if (err.code && err.code.startsWith("auth/")) {
        // Direct Firebase error object
        errorMessage = getFirebaseErrorMessage(err);
      } else if (err.message && err.message.includes("auth/")) {
        // Firebase error wrapped in Error message like "Firebase: Error (auth/email-already-in-use)"
        // Extract the error code from the message
        const match = err.message.match(/auth\/[\w\-]+/);
        if (match) {
          const firebaseErr = { code: match[0] };
          errorMessage = getFirebaseErrorMessage(firebaseErr);
        } else {
          errorMessage = err.message.replace(/Firebase:\s*Error\s*\(/, "").replace(/\)/, "");
        }
      } else if (err instanceof Error) {
        // It's a regular error (likely from our backend error handling)
        errorMessage = err.message;
      } else {
        // Fallback for unknown errors
        errorMessage = err?.message || "An unexpected error occurred. Please try again.";
      }
      
      setMessage(errorMessage);
      setIsError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-container">
      <form className="create-form" onSubmit={submit}>
        <h1>{title}</h1>
        <div className="form-group">
          <label htmlFor="firstName">First Name:</label>
          <input
            id="firstName"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="lastName">Last Name:</label>
          <input
            id="lastName"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="email">Email:</label>
          <input
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            type="email"
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="phoneNumber">Phone Number:</label>
          <input
            id="phoneNumber"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            type="tel"
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="dateOfBirth">Date of Birth:</label>
          <input
            id="dateOfBirth"
            value={dateOfBirth}
            onChange={(e) => setDateOfBirth(e.target.value)}
            type="date"
            min="1900-01-01"
            max="2099-12-31"
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="displayName">Display name:</label>
          <input
            id="displayName"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
        </div>

        {showCode && (
          <div className="form-group">
            <label htmlFor="code">Invitation Code:</label>
            <input
              id="code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              type="text"
              required
              placeholder="Enter your invitation code"
            />
          </div>
        )}

        <div className="form-group">
          <label htmlFor="password">Password:</label>
          <input
            id="password"
            value={password}
            onChange={handlePasswordChange}
            type="password"
            required
          />
          <div className="password-rules">
            <p className="password-rules-title">Password Requirements:</p>
            <ul className="password-rules-list">
              <li className={passwordRulesStatus.minLength ? "rule-met" : "rule-unmet"}>
                ✓ At least 8 characters
              </li>
              <li className={passwordRulesStatus.hasNumber ? "rule-met" : "rule-unmet"}>
                ✓ At least 1 number (0-9)
              </li>
              <li className={passwordRulesStatus.hasUppercase ? "rule-met" : "rule-unmet"}>
                ✓ At least 1 uppercase letter (A-Z)
              </li>
              <li className={passwordRulesStatus.hasLowercase ? "rule-met" : "rule-unmet"}>
                ✓ At least 1 lowercase letter (a-z)
              </li>
              <li className={passwordRulesStatus.hasSymbol ? "rule-met" : "rule-unmet"}>
                ✓ At least 1 symbol (!@#$%^&*...)
              </li>
            </ul>
          </div>
        </div>
        <button type="submit" className="create-form-button" disabled={loading}>
          {loading ? 'Creating Account...' : buttonText}
        </button>
        <button
          type="button"
          className="login-account-btn"
          onClick={onNavigateToLogin}
          disabled={loading}
        >
          Have an Account? Login
        </button>
      </form>

      {message && (
        <p className={`message ${isError ? "error" : "success"}`}>{message}</p>
      )}

      {onNavigateToOtherPage && (
        <div className="form-switch-container">
          <p className="form-switch-text">
            {showCode ? "Creating a regular user account?" : "Creating a PSW account?"}
          </p>
          <button
            type="button"
            className="form-switch-button"
            onClick={onNavigateToOtherPage}
          >
            {showCode ? "Create User" : "Create PSW"}
          </button>
        </div>
      )}
    </div>
  );
}
